import axios from "axios"
import React, { use, useEffect, useState } from "react"
import LoadingAnimation from "../LoadingAnimation"

interface ScreeningDocument {
    name: string;
    coachId: number;
    summary: string;
    firstLink: string;
    secondLink: string;
    thirdLink: string;
    certified: boolean;
    certificationFile: string;
    recordCheck: boolean;
}

export default function CoachScreeningDocument({csrfToken}:any){
    const [screeningDocument, setScreeningDocument] = useState<ScreeningDocument | null>(null)
    const [currentDecision, setCurrentDecision] = useState<'approve' | 'deny' | undefined>(undefined)
    const [loading, setLoading] = useState<boolean>(false)

    // Load screening document upon initial render
    useEffect(()=>{
        getScreeningDocument()
    },[])

    // Function to retrieve latest document
    function getScreeningDocument(){
        axios.get(`${process.env.REACT_APP_SKILLJA_URL}/screening/get_coach_screenings/`, {
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        })
        .then(res => {
            // Store screening document if it exists
            if (res.status === 200 && res.data.document !== null){
                setScreeningDocument(res.data.document)
            }
        })
        .catch(error => console.error("Error retrieving active users", error))
    }

    // Function to update screeing document status
    function UpdateDocumentStatus(){
        // Set loading state while API is called
        setLoading(true)
        const data = {
            decision: currentDecision,
            id: screeningDocument?.coachId
        }

        axios.post(`${process.env.REACT_APP_SKILLJA_URL}/screening/update_coach_screening/`, data, {
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        })
        .then(res => {
            if (res.status === 200) {
                // Clear previous document and decisions
                setScreeningDocument(null)
                setCurrentDecision(undefined)

                // Retrieve a new document to review
                getScreeningDocument()
            }
            // Delay loading animation for UX update
            const timer = setTimeout(() => {
                setLoading(false)
            }, 2000)
            return ()=> clearTimeout(timer)
        })
        .catch(error => console.error("Error retrieving active users", error))
        .finally(()=>{
            setLoading(false);
        })
    }

    return (
        <div>
            <section className="flex flex-col">
                <h3 className="mx-auto underline font-bold">Coach Screening Document</h3>
                <p className="my-2.5 mx-auto text-center text-sm font-semibold">
                    Please upload a screenshot of this document before approval. ( SkillJa - Google Drive, name file in format:
                    <span className="italic"> coach name - ID - dd/mm/yyyy </span>
                    )
                </p>
                {loading ?
                    <div className="flex h-80 w-full justify-center items-center m-auto my-2">
                        <LoadingAnimation />
                    </div>
                : 
                    (   screeningDocument === null ?
                        <div className="flex flex-col justify-center min-h-80 mx-auto w-full my-2 py-2 px-1 bg-gray-100 rounded-lg">
                            <p className="m-auto">No document to display</p>
                        </div>
                    :
                        <div className="flex flex-col min-h-80 mx-auto w-full my-2 py-2 px-1 bg-gray-100 rounded-lg">
                            <p className="text-sm px-2 mb-3">Summary: {screeningDocument.summary}</p>
                            <div className="grid grid-cols-2 gap-2 mt-auto text-sm">
                                <p className="p-2">Name: {screeningDocument.name}</p>
                                <p className="p-2">ID: {screeningDocument.coachId}</p>
                                <p className="p-2">Link #1 - 
                                    <a 
                                        href={`${(screeningDocument?.firstLink && screeningDocument.firstLink.length > 3) && screeningDocument.firstLink} `}
                                        target="blank" 
                                        className={`${screeningDocument?.firstLink && screeningDocument.firstLink.length > 3 ? 
                                            'text-main-green-600 cursor-pointer' : 
                                            'text-black cursor-not-allowed'}`
                                        }
                                    > 
                                        View
                                    </a>
                                </p>
                                <p className="p-2">Link #2 - 
                                    <a 
                                        href={`${(screeningDocument?.secondLink && screeningDocument.secondLink.length > 3) && screeningDocument.secondLink} `}
                                        target="blank" 
                                        className={`${screeningDocument?.secondLink && screeningDocument.secondLink.length > 3 ? 
                                            'text-main-green-600 cursor-pointer' : 
                                            'text-black cursor-not-allowed'}`
                                        }
                                    > 
                                        View
                                    </a>
                                </p>
                                <p className="p-2">Link #3 - 
                                    <a 
                                        href={`${(screeningDocument?.thirdLink && screeningDocument.thirdLink.length > 3) && screeningDocument.thirdLink} `}
                                        target="blank" 
                                        className={`${screeningDocument?.thirdLink && screeningDocument.thirdLink.length > 3 ? 
                                            'text-main-green-600 cursor-pointer' : 
                                            'text-black cursor-not-allowed'}`
                                        }
                                    > 
                                        View
                                    </a>
                                </p>
                                <p className="p-2">Certified: {screeningDocument.certified}</p>
                                <p className="p-2">Certification File - 
                                    <a 
                                        href={`${(screeningDocument?.certificationFile && screeningDocument.certificationFile.length > 3) && screeningDocument.certificationFile} `}
                                        target="blank" 
                                        className={`${screeningDocument?.certificationFile && screeningDocument.certificationFile.length > 3 ? 
                                            'text-main-green-600 cursor-pointer' : 
                                            'text-black cursor-not-allowed'}`
                                        }
                                    > 
                                        View
                                    </a>
                                </p>
                                <p className="p-2">Record Check: {screeningDocument.recordCheck}</p>
                            </div>
                        </div>
                    )
                }
            </section>
            <section className="flex justify-center my-4 w-full">
                {currentDecision === undefined ?
                <>
                    <button 
                        className="form-btn py-2 px-5 mx-2.5 rounded-lg text-sm" 
                        onClick={()=>setCurrentDecision('approve')}
                        disabled={loading || screeningDocument === null}
                    >
                        Approve
                    </button>
                    <button 
                        className="form-btn bg-red-600 hover:bg-red-800 py-2 px-5 mx-2.5 rounded-lg text-sm" 
                        onClick={()=>setCurrentDecision('deny')}
                        disabled={loading || screeningDocument === null}
                    >
                        Deny
                    </button>
                </>
                :
                <>
                    <button 
                        className={`form-btn py-2 px-5 mx-2.5 rounded-lg text-sm
                                ${currentDecision && currentDecision === 'approve' ? 
                                    'bg-main-green-600 hover:bg-main-green-800' : 
                                    'bg-red-700 hover:bg-red-900'} 
                        `}
                        onClick={()=>UpdateDocumentStatus()}
                        disabled={loading || screeningDocument === null}
                    >
                        Confirm {currentDecision && currentDecision === 'approve' ? 'Approval' : 'Denial'}
                    </button>
                    <button 
                        className="form-btn bg-main-grey-300 hover:bg-main-grey-500 py-2 px-5 mx-2.5 rounded-lg text-sm" 
                        onClick={()=>{
                            setCurrentDecision(undefined);
                        }}
                        disabled={loading || screeningDocument === null}
                    >
                        Need more time
                    </button>
                </>
                }
            </section>
        </div>
    )
}